import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { Check, Signpost } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { cn } from '../lib/utils'
import { totemBorderClass, totemBorderVars } from '../lib/totemBorder'
import type { TotemMarking, PlacedTotem } from '../hooks/useTotemSlots'

interface Props {
  setId: string
  setName: string
  totemMarking: TotemMarking
  placedTotems?: PlacedTotem[]
}

export function TotemAction({ setId, setName: name, totemMarking, placedTotems }: Props) {
  const placedTotemIds = new Set(placedTotems?.map(p => p.totem.id) ?? [])
  const creatableTotems = totemMarking.eligibleTotems

  if (creatableTotems.length === 0) return null

  const toggleSlot = (groupId: string, totemId: string, totemEmoji: string) => {
    const isActive = placedTotemIds.has(totemId)
    if (isActive) {
      totemMarking.removeSlot(groupId, totemId, setId)
      toast.success(
        `${totemEmoji} ` +
          fbt(
            'Totem removed from ' + fbt.param('setName', name),
            'Toast confirming the group totem was unmarked from a set',
          ),
      )
    } else {
      totemMarking.addSlot(groupId, totemId, setId)
      toast.success(
        `${totemEmoji} ` +
          fbt(
            'Totem marked at ' + fbt.param('setName', name),
            'Toast confirming the group totem was marked at a set',
          ),
      )
    }
  }

  if (creatableTotems.length === 1) {
    const { group, totem: t } = creatableTotems[0]
    const active = placedTotemIds.has(t.id)
    return (
      <button
        type="button"
        onClick={() => toggleSlot(group.id, t.id, t.emoji)}
        className={cn(
          // Emoji glyphs carry their own side bearing, so the left pad is
          // trimmed to keep the pill visually centred.
          'cursor-pointer inline-flex items-center gap-1 rounded-full border pl-2 pr-3 py-1 text-xs font-medium transition-colors',
          active
            ? cn('relative bg-card text-foreground', totemBorderClass(t.borderStyle))
            : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-primary',
        )}
        style={active ? totemBorderVars(t.borderStyle) : undefined}
        aria-label={fbt('Toggle totem', 'Accessible label for the totem toggle button on a set card')}
      >
        <span className="text-sm leading-none">{t.emoji}</span>
        {fbt('Totem', 'Label for the totem assignment button in the set detail dialog')}
      </button>
    )
  }

  const activeTotem = creatableTotems.find(e => placedTotemIds.has(e.totem.id))
  const activeBorder = activeTotem?.totem.borderStyle
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'cursor-pointer inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            activeBorder
              ? cn('relative bg-card text-foreground', totemBorderClass(activeBorder))
              : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-primary',
          )}
          style={activeBorder ? totemBorderVars(activeBorder) : undefined}
          aria-label={fbt('Assign totem', 'Accessible label for the totem picker button on a set card')}
        >
          <Signpost className="size-3.5" />
          {fbt('Totem', 'Label for the totem assignment button in the set detail dialog')}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-1" align="end" side="bottom">
        <ul className="flex flex-col">
          {creatableTotems.map(({ group, totem: t }) => {
            const active = placedTotemIds.has(t.id)
            return (
              <li key={t.id}>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm cursor-pointer hover:bg-secondary transition-colors"
                  onClick={() => toggleSlot(group.id, t.id, t.emoji)}
                >
                  <span className="text-base leading-none">{t.emoji}</span>
                  <span className="flex-1 truncate">{t.name}</span>
                  {active && <Check className="size-3.5 text-primary shrink-0" />}
                </button>
              </li>
            )
          })}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
